import type { MutatingScreenKind } from "@glide/app-description";
import {
    ArrayTransformKind,
    ScreenDescriptionKind,
    type ClassScreenDescription,
    type ComponentDescription,
    type PropertyDescription,
} from "@glide/app-description";
import { ErrorInfo, isErrorInfo, type ErrorOr } from "@glide/error-info";
import { makeTableName, makeTableRef, type Description } from "@glide/type-schema";
import { screenTitlePropertyDescriptor } from "./components/descriptor-utils";
import {
    getMutatingKindForScreen,
    getPropertyDescription,
    isMultiCasePropertyDescriptor,
    updatesForProperty,
    type AppDescriptionContext,
    type PropertyDescriptor,
} from "@glide/function-utils";
import { mapFilterUndefined } from "@glideapps/ts-necessities";
import { handlerForPropertyKind } from "./components/description-handlers";
import { handlerForComponentKind } from "./handlers";
import { getSubComponentsWithTables } from "./sub-components";
import { inputOutputTablesForClassOrArrayScreen } from "./description-utils";
import type { InputOutputTables } from "@glide/common-core/dist/js/description";
import { isInlineListComponentDescription, makeInlineListFormatPropertyDescriptor } from "./components/inline-list";

function normalizePropertyDescription(descr: PropertyDescriptor, value: unknown): PropertyDescription | undefined {
    if (isMultiCasePropertyDescriptor(descr)) {
        for (const pdc of descr.cases) {
            const handler = handlerForPropertyKind(pdc.kind);
            const normalized = handler.normalizeDescription(pdc, value);
            if (normalized === undefined) continue;
            return normalized;
        }
        return undefined;
    } else {
        const handler = handlerForPropertyKind(descr.kind);
        return handler.normalizeDescription(descr, value);
    }
}

export function normalizeDescription(
    propertyDescriptors: readonly PropertyDescriptor[],
    description: Description
): Description {
    let normalizedDescription: Description = {};

    for (const pd of propertyDescriptors) {
        const desc = getPropertyDescription(description, pd);
        if (desc === undefined) continue;
        const normalized = normalizePropertyDescription(pd, desc);
        if (normalized === undefined) continue;

        normalizedDescription = {
            ...normalizedDescription,
            ...updatesForProperty(pd.property, normalizedDescription, normalized, undefined),
        };
    }

    return normalizedDescription;
}

function normalizeComponentDescription(
    adc: AppDescriptionContext,
    component: ComponentDescription,
    tables: InputOutputTables | undefined,
    mutatingScreenKind: MutatingScreenKind | undefined,
    isFirstComponent: boolean
): ErrorOr<ComponentDescription> {
    const handler = handlerForComponentKind(component.kind);
    if (handler === undefined) return new ErrorInfo(400, `Unknown component kind: ${component.kind}`);

    const componentDescriptor = handler.getDescriptor(
        component,
        tables,
        adc,
        mutatingScreenKind,
        false,
        isFirstComponent
    );

    const propertyDescriptors = [...componentDescriptor.properties];
    if (isInlineListComponentDescription(component)) {
        // FIXME: This is a hack to get the `format` property to show up.
        propertyDescriptors.unshift(makeInlineListFormatPropertyDescriptor(component, adc, tables, mutatingScreenKind));
    }

    let normalizedComponent: ComponentDescription = {
        ...normalizeDescription(propertyDescriptors, component),

        kind: component.kind,
        componentID: component.componentID,

        visibilityFilters: component.visibilityFilters?.filter(t => t.kind === ArrayTransformKind.Filter) ?? [],
    };

    for (const pd of propertyDescriptors) {
        const desc = getPropertyDescription(component, pd);
        if (desc === undefined) continue;
        const normalized = normalizePropertyDescription(pd, desc);
        if (normalized === undefined) continue;

        normalizedComponent = {
            ...updatesForProperty(pd.property, normalizedComponent, normalized, undefined),
            ...normalizedComponent,
        };
    }

    const maybeSubComponents = getSubComponentsWithTables(component, undefined, adc, undefined);
    if (maybeSubComponents !== undefined) {
        const [subComponents, subComponentTables, subComponentMutatingScreenKind] = maybeSubComponents;
        const normalizedSubComponents = mapFilterUndefined(subComponents, desc => {
            const normalized = normalizeComponentDescription(
                adc,
                desc,
                subComponentTables,
                subComponentMutatingScreenKind,
                false
            );
            if (isErrorInfo(normalized)) return undefined;
            return normalized;
        });
        normalizedComponent = { components: normalizedSubComponents, ...normalizedComponent } as ComponentDescription;
    }

    return normalizedComponent;
}

export function normalizeScreenDescription(
    adc: AppDescriptionContext,
    screenName: string
): ErrorOr<ClassScreenDescription> {
    const screenDescription = adc.appDescription?.screenDescriptions[screenName];
    if (screenDescription === undefined) {
        return new ErrorInfo(400, "Screen not found");
    }
    if (screenDescription?.kind !== ScreenDescriptionKind.Class) {
        return new ErrorInfo(400, "Screen is not a class screen");
    }

    // FIXME: support these
    if (screenDescription.isForm === true) return new ErrorInfo(400, "Form screens are not supported yet");

    const tables = inputOutputTablesForClassOrArrayScreen(screenDescription, adc.schema);
    const mutatingScreenKind = getMutatingKindForScreen(screenName, screenDescription);

    return {
        kind: ScreenDescriptionKind.Class,
        type: makeTableRef(makeTableName(screenDescription.type.tableName)),
        isForm: false,
        fetchesData: screenDescription.fetchesData ?? false,
        title: normalizePropertyDescription(screenTitlePropertyDescriptor, screenDescription.title),
        components: mapFilterUndefined(screenDescription.components, (desc, i) => {
            const normalized = normalizeComponentDescription(adc, desc, tables, mutatingScreenKind, i === 0);
            if (isErrorInfo(normalized)) return undefined;
            return normalized;
        }),
        transforms: screenDescription.transforms ?? [],
    };
}
